/* SaveAsDialog — shown when there is no chapter to auto-save to.
 *
 * Triggered by the loom:save-as-needed event (fired from useChapterMutations
 * when !chapter) or via the "Save As" action in the error save-state pill.
 *
 * The dialog creates a new chapter inside the active project, saves the
 * pending content there, and switches the editor to that chapter.
 */
import React, { useState, useEffect } from 'react';
import { FolderOpen, FileText } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { chaptersApi } from '@/lib/api';
import { usePW } from '@/lib/store';

export function SaveAsDialog({ open, onClose }) {
  const { project, setChapters, setActiveChapterId, chapters } = usePW();
  const [title,   setTitle]   = useState('');
  const [content, setContent] = useState('');
  const [saving,  setSaving]  = useState(false);

  // Populate content from loom:save-as-needed payload
  useEffect(() => {
    if (!open) { setTitle(''); setContent(''); setSaving(false); return; }
    const handler = (e) => {
      setContent(e.detail?.content || '');
    };
    window.addEventListener('loom:save-as-pending-content', handler);
    return () => window.removeEventListener('loom:save-as-pending-content', handler);
  }, [open]);

  const suggestedNumber = (chapters?.length || 0) + 1;

  const handleSave = async () => {
    if (!project?.id) { toast.error('No active project — open or create a project first.'); return; }
    setSaving(true);
    try {
      const newChapter = await chaptersApi.create(project.id);
      const saved = await chaptersApi.update(newChapter.id, {
        title:   title.trim() || `Chapter ${suggestedNumber}`,
        content: content || '',
      });
      setChapters?.((prev) => [...prev, saved]);
      setActiveChapterId?.(saved.id);
      toast.success(`Saved as "${saved.title || `Chapter ${suggestedNumber}`}".`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create chapter — check your connection and try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-sm"
        data-testid="save-as-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <FolderOpen className="h-4 w-4 text-primary shrink-0" />
            Name your manuscript
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground leading-relaxed">
          Your writing needs a home. Give this chapter a title and it will be
          saved to your project immediately.
        </p>

        {project && (
          <div className="flex items-center gap-1.5 rounded-md border border-border bg-secondary/40 px-3 py-2 text-[12px]">
            <FileText className="h-3 w-3 shrink-0 text-muted-foreground" />
            <span className="text-muted-foreground">Project:</span>
            <span className="font-medium text-foreground truncate">{project.title || 'My Story'}</span>
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="save-as-title" className="text-[12px]">Chapter title</Label>
          <Input
            id="save-as-title"
            autoFocus
            placeholder={`e.g. Chapter ${suggestedNumber} · The Arrival`}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !saving) handleSave(); }}
            data-testid="save-as-title-input"
          />
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving}
            data-testid="save-as-confirm-btn"
          >
            {saving ? 'Saving…' : 'Save chapter'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
